/**
 * Module #34 Phase A — the copilot surface (single entry point).
 *
 * Owns the one source of truth for the copilot UI: the insights hook, the
 * open/closed state, the ⌘/ shortcut, and the proactive auto-open. Renders the
 * floating launcher bubble when closed and the panel (insight brief + chat) when
 * open — never both, so nothing stacks. Mounted once by AdminChromeLayout.
 */

import { useEffect, useRef, useState } from 'react';
import { useCopilotInsights } from '../../hooks/useCopilotInsights';
import { CopilotBubble } from './CopilotBubble';
import { CopilotPanel } from './CopilotPanel';

const SEEN_KEY = 'auctisync_copilot_seen_insights';
const SEEN_CAP = 200;

function loadSeen(): Set<number> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr)
      ? new Set(arr.filter((n): n is number => typeof n === 'number'))
      : new Set();
  } catch {
    return new Set();
  }
}

function saveSeen(seen: Set<number>): void {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-SEEN_CAP)));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

export function Copilot() {
  const { insights, counts, dismiss, fireAction } = useCopilotInsights();
  const [open, setOpen] = useState(false);
  const seenRef = useRef<Set<number>>(loadSeen());

  // ⌘/ (Ctrl+/) toggles the panel — works from any admin page.
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key !== '/' || !(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable === true) return;
      e.preventDefault();
      setOpen((v) => !v);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Proactive auto-open: open the panel once when an unseen high/critical card
  // appears. Normal/low cards only bump the badge — helpful, not naggy.
  useEffect(() => {
    const urgentUnseen = insights.filter(
      (i) =>
        (i.priority === 'critical' || i.priority === 'high') && !seenRef.current.has(i.insightId),
    );
    if (urgentUnseen.length === 0) return;
    for (const i of urgentUnseen) seenRef.current.add(i.insightId);
    saveSeen(seenRef.current);
    setOpen(true);
  }, [insights]);

  return (
    <>
      <CopilotPanel
        open={open}
        onClose={() => setOpen(false)}
        insights={insights}
        onDismiss={(id) => void dismiss(id)}
        onFireAction={fireAction}
      />
      {!open && (
        <CopilotBubble
          count={counts.total}
          urgent={counts.highOrCritical > 0}
          onOpen={() => setOpen(true)}
        />
      )}
    </>
  );
}
